import { queryOptions, useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  NO_FACET_MATCH,
  boardCalendarQuerySchema,
  boardCalendarSchema,
  ulidSchema,
  type ApiError,
  type BoardCalendar,
  type BoardCalendarEntry,
  type BoardCalendarQuery,
} from '@molt/shared';
import { EMPTY_BOARD_FILTER, type BoardFilterSelection } from './board-filter';
import { parseCardTimestamp } from './card-dates';
import { get } from './api';
import {
  WEB_INTERFACE_MESSAGES,
  interfaceMessage,
  type InterfaceLanguageContextValue,
} from './interface-language';
import { queryKeys } from './query-keys';

export type BoardCalendarMode = 'month' | 'week';
export type CalendarWeekStartsOn = 0 | 1 | 2 | 3 | 4 | 5 | 6;
export type BoardCalendarTranslate = InterfaceLanguageContextValue['t'];

export interface BoardCalendarQueryInput {
  readonly from: string;
  readonly to: string;
  readonly filter?: BoardFilterSelection | undefined;
  readonly listIds?: readonly string[] | undefined;
}

export interface BoardCalendarGridOptions {
  readonly mode: BoardCalendarMode;
  readonly anchorDate: Date | string;
  readonly weekStartsOn?: CalendarWeekStartsOn | undefined;
}

export interface BoardCalendarDay {
  readonly key: string;
  readonly date: Date;
  readonly inAnchorMonth: boolean;
  readonly isToday: boolean;
}

export interface BoardCalendarWeek {
  readonly index: number;
  readonly days: readonly BoardCalendarDay[];
}

export interface BoardCalendarGrid {
  readonly mode: BoardCalendarMode;
  readonly anchorDate: Date;
  readonly from: string;
  readonly to: string;
  readonly weeks: readonly BoardCalendarWeek[];
  readonly days: readonly BoardCalendarDay[];
}

export interface BoardCalendarEntryRange {
  readonly startKey: string;
  readonly dueKey: string;
  readonly isSpan: boolean;
  readonly isInverted: boolean;
}

export interface BoardCalendarEntrySegment {
  readonly weekIndex: number;
  readonly startDayIndex: number;
  readonly endDayIndex: number;
  readonly startKey: string;
  readonly endKey: string;
  readonly span: number;
}

export interface BoardCalendarPlacedEntry {
  readonly entry: BoardCalendarEntry;
  readonly range: BoardCalendarEntryRange;
  readonly segments: readonly BoardCalendarEntrySegment[];
}

export interface BoardCalendarDatePatch {
  readonly startAt: string | null;
  readonly dueAt: string;
  /**
   * BE-44.7 (FR-EXP-029): carried through every drag/resize patch unchanged —
   * moving an all-day card must not silently convert it to a timed instant
   * (or vice versa). Calendar-day shifts preserve midnight-UTC, so the flag
   * stays valid on the new date.
   */
  readonly dueAllDay: boolean;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const INVALID_DATE_KEY = 'primitives.boardCalendar.invalidDateKey';
const INVALID_TIMESTAMP = 'primitives.boardCalendar.invalidTimestamp';

function sourceBoardCalendarMessage(
  key: string,
  values?: Record<string, string | number>,
  count?: number,
): string {
  const message = WEB_INTERFACE_MESSAGES[key];
  if (message === undefined) return key;
  return interfaceMessage(message, values, count);
}

export function boardCalendarSearchParams(
  input: BoardCalendarQueryInput,
): URLSearchParams {
  const parsed = boardCalendarQueryObject(input);
  const params = new URLSearchParams();
  params.set('from', parsed.from);
  params.set('to', parsed.to);

  for (const member of parsed.members ?? []) params.append('members', member);
  for (const label of parsed.labels ?? []) params.append('labels', label);
  for (const due of parsed.due ?? []) params.append('due', due);
  for (const completion of parsed.completion ?? [])
    params.append('completion', completion);
  if (parsed.activity !== undefined) params.set('activity', parsed.activity);
  if (parsed.keyword !== undefined) params.set('keyword', parsed.keyword);
  for (const listId of parsed.lists ?? []) {
    params.append('lists', listId);
  }

  return params;
}

export function boardCalendarQueryCacheKey(input: BoardCalendarQueryInput): string {
  return boardCalendarSearchParams(input).toString();
}

export function getBoardCalendar(
  boardId: string,
  input: BoardCalendarQueryInput,
): Promise<BoardCalendar> {
  const search = boardCalendarSearchParams(input).toString();
  return get(
    `/boards/${encodeURIComponent(boardId)}/calendar?${search}`,
    boardCalendarSchema,
  );
}

export function boardCalendarQueryOptions(
  boardId: string,
  input: BoardCalendarQueryInput,
) {
  const cacheKey = boardCalendarQueryCacheKey(input);
  return queryOptions({
    queryKey: queryKeys.boards.calendar(boardId, cacheKey),
    queryFn: () => getBoardCalendar(boardId, input),
  });
}

export function useBoardCalendar(
  boardId: string,
  input: BoardCalendarQueryInput,
  enabled = true,
): UseQueryResult<BoardCalendar, ApiError> {
  const cacheKey = boardCalendarQueryCacheKey(input);
  return useQuery<BoardCalendar, ApiError>({
    queryKey: queryKeys.boards.calendar(boardId, cacheKey),
    queryFn: () => getBoardCalendar(boardId, input),
    enabled,
  });
}

/**
 * `FR-NOTIF-032` (`BE-44.8`) -- the week start is DERIVED, once, from the
 * member's stored language via `weekStartForLocale` (`lib/locale-formatting.ts`),
 * and threaded in by every caller: `BoardFace` passes the same value to the
 * query-range grids here and to both `<BoardCalendarView>` renders, whose own
 * default derives it when the prop is omitted, and the workspace calendar
 * orders its header and grid by it. Keep it that way: deriving it at any one
 * site alone reopens the split-brain this closed, where an `en-US` member
 * fetches a Sunday-first week and is drawn a Monday-first one, silently losing
 * the first- and last-day entries with no error and nothing red.
 *
 * The `= 1` default below is continuity for direct callers, not a product
 * decision: bare `en` (the default language) is Monday-first, so it agrees
 * with the derived value for every member who has never chosen a language.
 */
export function buildBoardCalendarGrid({
  mode,
  anchorDate,
  weekStartsOn = 1,
}: BoardCalendarGridOptions): BoardCalendarGrid {
  const anchor = parseAnchorDate(anchorDate);
  const todayKey = calendarDateKey(new Date());
  const firstVisible =
    mode === 'month'
      ? startOfCalendarWeek(
          new Date(anchor.getFullYear(), anchor.getMonth(), 1),
          weekStartsOn,
        )
      : startOfCalendarWeek(anchor, weekStartsOn);
  const dayCount = mode === 'month' ? 42 : 7;
  const days = Array.from({ length: dayCount }, (_, index) => {
    const date = addCalendarDays(firstVisible, index);
    const key = calendarDateKey(date);
    return {
      key,
      date,
      inAnchorMonth: date.getMonth() === anchor.getMonth(),
      isToday: key === todayKey,
    };
  });
  const weeks = Array.from({ length: dayCount / 7 }, (_, index) => ({
    index,
    days: days.slice(index * 7, index * 7 + 7),
  }));

  return {
    mode,
    anchorDate: anchor,
    from: firstVisible.toISOString(),
    to: addCalendarDays(firstVisible, dayCount).toISOString(),
    weeks,
    days,
  };
}

export function placeBoardCalendarEntries(
  entries: readonly BoardCalendarEntry[],
  days: readonly BoardCalendarDay[],
): readonly BoardCalendarPlacedEntry[] {
  const indexByKey = new Map(days.map((day, index) => [day.key, index]));
  const placements: BoardCalendarPlacedEntry[] = [];

  for (const entry of entries) {
    const range = boardCalendarEntryRange(entry);
    if (range === null) continue;

    const firstIndex = indexByKey.get(range.startKey);
    const lastIndex = indexByKey.get(range.dueKey);
    const visibleFirst =
      firstIndex ?? firstVisibleIndexForRange(days, range.startKey, range.dueKey);
    const visibleLast =
      lastIndex ?? lastVisibleIndexForRange(days, range.startKey, range.dueKey);
    if (visibleFirst === null || visibleLast === null || visibleFirst > visibleLast) {
      continue;
    }

    const segments: BoardCalendarEntrySegment[] = [];
    for (
      let weekIndex = Math.floor(visibleFirst / 7);
      weekIndex <= Math.floor(visibleLast / 7);
      weekIndex += 1
    ) {
      const weekStart = weekIndex * 7;
      const segmentStart = Math.max(visibleFirst, weekStart);
      const segmentEnd = Math.min(visibleLast, weekStart + 6);
      segments.push({
        weekIndex,
        startDayIndex: segmentStart - weekStart,
        endDayIndex: segmentEnd - weekStart,
        startKey: days[segmentStart]!.key,
        endKey: days[segmentEnd]!.key,
        span: segmentEnd - segmentStart + 1,
      });
    }

    placements.push({ entry, range, segments });
  }

  return placements;
}

export function boardCalendarEntryRange(
  entry: BoardCalendarEntry,
): BoardCalendarEntryRange | null {
  const dueKey = timestampDateKey(entry.dueAt);
  if (dueKey === null) return null;

  if (entry.type !== 'card' || entry.startAt === null) {
    return { startKey: dueKey, dueKey, isSpan: false, isInverted: false };
  }

  const rawStartKey = timestampDateKey(entry.startAt);
  if (rawStartKey === null) {
    return { startKey: dueKey, dueKey, isSpan: false, isInverted: false };
  }

  const isInverted = rawStartKey > dueKey;
  const startKey = isInverted ? dueKey : rawStartKey;
  const endKey = isInverted ? rawStartKey : dueKey;
  return {
    startKey,
    dueKey: endKey,
    isSpan: startKey !== endKey,
    isInverted,
  };
}

export function calendarDateKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function calendarDateFromKey(
  key: string,
  t: BoardCalendarTranslate = sourceBoardCalendarMessage,
): Date {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (match === null) throw new Error(t(INVALID_DATE_KEY, { key }));
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

export function timestampDateKey(value: string): string | null {
  const parsed = parseCardTimestamp(value);
  return parsed === null ? null : calendarDateKey(parsed);
}

export function addCalendarDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return startOfLocalDay(next);
}

export function diffCalendarDays(fromKey: string, toKey: string): number {
  return Math.round((utcTimeForDateKey(toKey) - utcTimeForDateKey(fromKey)) / DAY_MS);
}

export function replaceTimestampDate(
  value: string,
  dateKey: string,
  t: BoardCalendarTranslate = sourceBoardCalendarMessage,
): string {
  const parsed = parseCardTimestamp(value);
  if (parsed === null) throw new Error(t(INVALID_TIMESTAMP));
  const date = calendarDateFromKey(dateKey, t);
  date.setHours(
    parsed.getHours(),
    parsed.getMinutes(),
    parsed.getSeconds(),
    parsed.getMilliseconds(),
  );
  return date.toISOString();
}

export function dueDateForCalendarDate(
  dateKey: string,
  t: BoardCalendarTranslate = sourceBoardCalendarMessage,
): string {
  const date = calendarDateFromKey(dateKey, t);
  date.setHours(17, 0, 0, 0);
  return date.toISOString();
}

export function shiftTimestampByCalendarDays(
  value: string,
  days: number,
  t: BoardCalendarTranslate = sourceBoardCalendarMessage,
): string {
  const parsed = parseCardTimestamp(value);
  if (parsed === null) throw new Error(t(INVALID_TIMESTAMP));
  parsed.setDate(parsed.getDate() + days);
  return parsed.toISOString();
}

export function boardCalendarReschedulePatch(
  entry: BoardCalendarEntry,
  toDateKey: string,
  t: BoardCalendarTranslate = sourceBoardCalendarMessage,
): BoardCalendarDatePatch | null {
  if (entry.type !== 'card') return null;

  const dueKey = timestampDateKey(entry.dueAt);
  if (dueKey === null) return null;
  const dayDelta = diffCalendarDays(dueKey, toDateKey);

  return {
    startAt:
      entry.startAt === null
        ? null
        : shiftTimestampByCalendarDays(entry.startAt, dayDelta, t),
    dueAt: shiftTimestampByCalendarDays(entry.dueAt, dayDelta, t),
    dueAllDay: entry.dueAllDay,
  };
}

export function boardCalendarResizePatch(
  entry: BoardCalendarEntry,
  edge: 'start' | 'due',
  toDateKey: string,
  t: BoardCalendarTranslate = sourceBoardCalendarMessage,
): BoardCalendarDatePatch | null {
  if (entry.type !== 'card') return null;

  const startAt =
    edge === 'start'
      ? replaceTimestampDate(entry.startAt ?? entry.dueAt, toDateKey, t)
      : entry.startAt;
  const dueAt =
    edge === 'due' ? replaceTimestampDate(entry.dueAt, toDateKey, t) : entry.dueAt;

  const startKey = startAt === null ? null : timestampDateKey(startAt);
  const dueKey = timestampDateKey(dueAt);
  if (dueKey === null || (startKey !== null && dueKey < startKey)) return null;

  return { startAt, dueAt, dueAllDay: entry.dueAllDay };
}

function boardCalendarQueryObject(input: BoardCalendarQueryInput): BoardCalendarQuery {
  const filter = input.filter ?? EMPTY_BOARD_FILTER;
  const query: Record<string, unknown> = {
    from: input.from,
    to: input.to,
  };
  const members = normaliseFacetOptions(filter.members);
  const labels = normaliseFacetOptions(filter.labels);
  if (members.length > 0) query.members = members;
  if (labels.length > 0) query.labels = labels;
  if (filter.due.length > 0) query.due = filter.due;
  if (filter.completion.length > 0) query.completion = filter.completion;
  if (filter.activity !== null) query.activity = filter.activity;
  if (filter.keyword.trim() !== '') query.keyword = filter.keyword.trim();

  const lists = normaliseUlids(input.listIds ?? []);
  if (lists.length > 0) query.lists = lists;

  return boardCalendarQuerySchema.parse(query);
}

function normaliseFacetOptions(values: readonly string[]): string[] {
  return Array.from(
    new Set(
      values.filter(
        (value) => value === NO_FACET_MATCH || ulidSchema.safeParse(value).success,
      ),
    ),
  ).sort();
}

function normaliseUlids(values: readonly string[]): string[] {
  return Array.from(
    new Set(values.filter((value) => ulidSchema.safeParse(value).success)),
  ).sort();
}

function parseAnchorDate(value: Date | string): Date {
  if (value instanceof Date) return startOfLocalDay(value);
  return startOfLocalDay(calendarDateFromKey(value));
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function startOfCalendarWeek(date: Date, weekStartsOn: CalendarWeekStartsOn): Date {
  const start = startOfLocalDay(date);
  const diff = (start.getDay() - weekStartsOn + 7) % 7;
  start.setDate(start.getDate() - diff);
  return start;
}

function firstVisibleIndexForRange(
  days: readonly BoardCalendarDay[],
  startKey: string,
  dueKey: string,
): number | null {
  const index = days.findIndex((day) => day.key >= startKey && day.key <= dueKey);
  return index === -1 ? null : index;
}

function lastVisibleIndexForRange(
  days: readonly BoardCalendarDay[],
  startKey: string,
  dueKey: string,
): number | null {
  for (let index = days.length - 1; index >= 0; index -= 1) {
    const key = days[index]!.key;
    if (key >= startKey && key <= dueKey) return index;
  }
  return null;
}

function utcTimeForDateKey(key: string): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (match === null) throw new Error(`Invalid calendar date key: ${key}`);
  return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}
